import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthProvider'
import { PERM } from '@/shared/auth/types'
import { cn } from '@/shared/ui/primitives'
import { Icon, type IconName } from '@/app/icons'
import { TONE_CLASS, TONE_VAR, type ModuleDef } from '@/app/modules'
import { visibleSections } from '@/app/nav'
import { CARD } from './ui'

/**
 * Up to this many modules, the cards fit the side column. Past it they move
 * to a full-width grid under the tiles -- a column of five tall cards pushes
 * the shortcuts off the screen. See docs/konvensi-ui.md.
 */
export const SIDE_COLUMN_MAX_MODULES = 4

export function ModuleCard({ module }: { module: ModuleDef }) {
  const { can } = useAuth()
  const navigate = useNavigate()
  const tone = TONE_CLASS[module.tone]
  const external = module.kind === 'external'

  // The module's main pages, as a preview of what is inside -- filtered by
  // permission like the menu itself, so the card never promises a page the
  // person cannot open.
  const pages =
    module.kind === 'internal'
      ? (visibleSections(module.nav, can).find((section) => section.title === module.short)?.items ?? []).map(
          (item) => item.label,
        )
      : []

  return (
    <button
      type="button"
      onClick={() => {
        if (module.kind === 'internal') navigate(module.basePath)
        else window.open(module.href(), '_blank', 'noopener,noreferrer')
      }}
      className={cn(
        CARD,
        'group relative block w-full overflow-hidden p-5 text-left transition hover:-translate-y-0.5 hover:shadow-card-lg',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
      )}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-21"
        style={{
          background: `radial-gradient(90% 120% at 100% 0%, color-mix(in srgb, ${TONE_VAR[module.tone]} 22%, transparent), transparent 70%)`,
        }}
      />
      {/* The oversized mark bleeding off the corner, rather than a stock
          illustration: it scales, needs no asset, and cannot end up looking
          like another product's artwork. */}
      <Icon
        name={module.icon}
        className={cn('pointer-events-none absolute -top-7 -right-7 size-32 opacity-[0.09] transition group-hover:-rotate-6 group-hover:scale-105', tone.text)}
      />

      <span className={cn('relative grid size-11 place-items-center rounded-xl shadow-[0_8px_18px_-8px_currentColor]', tone.solid)}>
        <Icon name={module.icon} className="size-5.5" />
      </span>
      <h3 className="relative mt-3.5 text-[1.05rem] font-bold text-gray-900">{module.name}</h3>
      <p className="relative mt-1 text-gray-500">{module.description}</p>

      {pages.length > 0 && (
        <div className="relative mt-3.5 flex flex-wrap gap-1.5">
          {pages.map((page) => (
            <span key={page} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
              {page}
            </span>
          ))}
        </div>
      )}

      <div className="relative mt-4 flex flex-wrap items-center gap-2.5">
        <b className={cn('inline-flex items-center gap-1.5 font-semibold', tone.text)}>
          {external ? 'Buka di tab baru' : `Buka ${module.short}`}
          <Icon name={external ? 'external' : 'arrowRight'} className="size-4 transition group-hover:translate-x-0.5" />
        </b>
        {/* Honest label: until single sign-on, another app means another login. */}
        {external && <small className="text-xs text-gray-500">Perlu masuk sekali lagi</small>}
      </div>
    </button>
  )
}

/**
 * The places people go most, by permission. Every one is also in the menu
 * and in Ctrl K: a shortcut that is the only route somewhere hides it.
 */
export function Shortcuts() {
  const { can } = useAuth()
  const items: Array<{ to: string; label: string; icon: IconName }> = []
  if (can(PERM.BILLING_PROFORMA, 'add')) items.push({ to: '/billing/proforma/new', label: 'Buat proforma baru', icon: 'plus' })
  if (can(PERM.BILLING_APPROVAL)) items.push({ to: '/billing/approvals', label: 'Antrian persetujuan', icon: 'check' })
  if (can(PERM.BILLING_PROFORMA)) items.push({ to: '/billing/proforma', label: 'Daftar proforma', icon: 'document' })
  if (can(PERM.BILLING_INVOICE)) items.push({ to: '/billing/invoices', label: 'Daftar invoice', icon: 'receipt' })
  if (can(PERM.BILLING_CUSTOMER)) items.push({ to: '/billing/customers', label: 'Pelanggan', icon: 'building' })
  if (can(PERM.BILLING_RECON)) items.push({ to: '/billing/reconciliation', label: 'Rekonsiliasi', icon: 'scale' })
  if (items.length === 0) return null

  return (
    <section className={CARD} aria-label="Pintasan">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-base font-bold tracking-tight text-gray-900">Pintasan</h2>
      </div>
      <ul className="p-1.5">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-900"
            >
              <span className="grid size-8 place-items-center rounded-lg bg-gray-100 text-gray-700 group-hover:bg-primary-light group-hover:text-primary">
                <Icon name={item.icon} className="size-4" />
              </span>
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function SearchTip() {
  return (
    <div className={cn(CARD, 'flex items-start gap-3 px-4 py-3.5 text-[0.8125rem] text-gray-500')}>
      <Icon name="bolt" className="mt-0.5 size-4 shrink-0 text-primary" />
      <p>
        Tekan <Kbd>Ctrl</Kbd> <Kbd>K</Kbd> dari halaman mana pun untuk <b className="font-semibold text-gray-900">membuka dokumen</b>{' '}
        atau lompat ke menu.
      </p>
    </div>
  )
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="rounded border border-b-2 border-gray-300 bg-card px-1.5 py-px font-sans text-[0.7rem] text-gray-600">
      {children}
    </kbd>
  )
}

/**
 * A role with no module at all. The message to send is written out, so the
 * person does not have to work out what to ask for.
 */
export function NoAccessCard({ name, roleName }: { name?: string; roleName?: string }) {
  const message = `Mohon akses SIPAY untuk ${name ?? 'saya'} (peran: ${roleName ?? '-'}).`
  const codeRef = useRef<HTMLElement>(null)
  const [copied, setCopied] = useState<'done' | 'selected' | null>(null)

  function selectText() {
    const node = codeRef.current
    if (!node) return
    const range = document.createRange()
    range.selectNodeContents(node)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    setCopied('selected')
  }

  function copy() {
    // The clipboard API can be refused (http origin, browser policy); the
    // fallback selects the text so Ctrl C still works.
    if (!navigator.clipboard) return selectText()
    navigator.clipboard.writeText(message).then(() => setCopied('done'), selectText)
  }

  return (
    <section className={cn(CARD, 'mt-7 mb-14')}>
      <div className="flex items-center gap-4 p-7">
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-gray-200 text-gray-500">
          <Icon name="lock" className="size-5" />
        </span>
        <div>
          <p className="font-semibold text-gray-900">Belum ada modul untuk peran Anda</p>
          <p className="mt-0.5 text-gray-500">
            Peran <b className="text-gray-900">{roleName}</b> belum diberi akses ke modul mana pun. Kirim permintaan di
            bawah ke administrator SIPAY.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2.5 px-7 pb-7">
        <code ref={codeRef} className="min-w-0 flex-[1_1_16rem] rounded-lg bg-gray-100 px-3 py-2 font-mono text-[0.8rem] text-gray-700 select-all">
          {message}
        </code>
        <button
          type="button"
          onClick={copy}
          className="h-9 rounded-lg bg-gray-200 px-3.5 font-semibold text-gray-900 hover:bg-primary-light hover:text-primary"
        >
          {copied === 'done' ? 'Tersalin' : 'Salin permintaan akses'}
        </button>
        {copied === 'selected' && <span className="text-xs text-gray-500">Teks sudah dipilih, tekan Ctrl C.</span>}
      </div>
    </section>
  )
}
